import React, { useState, useEffect } from 'react';
import { Lock, Mail, Send, BarChart3, Inbox, MessageSquare, Power, Bot, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  AdminDashboardTab,
  AdminResponseTab,
  AdminTicketsTab,
  AdminEmailsTab,
  AdminMessagesTab,
  AdminTezzaTab,
  AdminPaymentsTab
} from '../components/admin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Dashboard state
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [emails, setEmails] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loadingStats, setLoadingStats] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  
  // Tezza activity state
  const [tezzaActivity, setTezzaActivity] = useState([]);
  const [tezzaStats, setTezzaStats] = useState(null);
  
  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  
  // Response tab pre-fill (from clicking on a message)
  const [responseInitial, setResponseInitial] = useState({ email: '', subject: '', message: '' });

  // Fetch maintenance status on auth
  useEffect(() => {
    if (isAuthenticated) {
      fetchMaintenanceStatus();
    }
  }, [isAuthenticated]);

  const fetchMaintenanceStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance`);
      const data = await res.json();
      setMaintenanceMode(data.enabled);
    } catch (error) {
      console.error('Failed to fetch maintenance status:', error);
    }
  };

  const toggleMaintenanceMode = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/maintenance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          enabled: !maintenanceMode
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setMaintenanceMode(data.enabled);
        toast.success(data.enabled ? 'Maintenance mode enabled' : 'Site is now live');
      }
    } catch (error) {
      toast.error('Failed to toggle maintenance mode');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password) {
      setIsAuthenticated(true);
      fetchStats();
    }
  };

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const [statsRes, ticketsRes, emailsRes, messagesRes, tezzaRes, paymentsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/stats?password=${password}`),
        fetch(`${API_URL}/api/admin/tickets?password=${password}&limit=10`),
        fetch(`${API_URL}/api/admin/emails?password=${password}&limit=10`),
        fetch(`${API_URL}/api/admin/messages?password=${password}&limit=20`),
        fetch(`${API_URL}/api/admin/tezza-activity?password=${password}&limit=20`),
        fetch(`${API_URL}/api/admin/payments?password=${password}&limit=100`)
      ]);
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }
      if (ticketsRes.ok) {
        const ticketsData = await ticketsRes.json();
        setTickets(ticketsData.tickets || []);
      }
      if (emailsRes.ok) {
        const emailsData = await emailsRes.json();
        setEmails(emailsData.tickets || []);
      }
      if (messagesRes.ok) {
        const messagesData = await messagesRes.json();
        setMessages(messagesData.tickets || []);
      }
      if (tezzaRes.ok) {
        const tezzaData = await tezzaRes.json();
        setTezzaActivity(tezzaData.activities || []);
        setTezzaStats(tezzaData.stats || null);
      }
      if (paymentsRes.ok) {
        const paymentsData = await paymentsRes.json();
        setPayments(paymentsData.payments || []);
        setPaymentsTotal(paymentsData.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Australia/Sydney'
    });
  };

  const handleGenerateResponseFromMessage = (msg) => {
    // Use a timestamp to force re-render even if same email clicked again
    setResponseInitial({
      email: msg.email,
      subject: msg.subject || 'Your message to Washed Away',
      message: msg.message,
      timestamp: Date.now()
    });
    setActiveTab('respond');
  };

  // Clear responseInitial when navigating away from respond tab
  const handleTabChange = (tab) => {
    if (tab !== 'respond') {
      setResponseInitial({ email: '', subject: '', message: '' });
    }
    setActiveTab(tab);
    fetchStats();
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-page" data-testid="admin-login">
        <div className="admin-login-container">
          <div className="admin-login-card">
            <Lock className="admin-lock-icon" />
            <h1>Admin Access</h1>
            <p>Enter password to access support tools</p>
            <form onSubmit={handleLogin}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="admin-input"
                autoFocus
                data-testid="admin-password-input"
              />
              <Button type="submit" className="admin-login-btn" data-testid="admin-login-btn">
                Access Dashboard
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      <div className="admin-container">
        <header className="admin-header">
          <div className="admin-header-top">
            <div>
              <h1>Washed Away Admin</h1>
              <p>Support tools & analytics dashboard</p>
            </div>
            <button 
              onClick={toggleMaintenanceMode}
              className={`maintenance-toggle ${maintenanceMode ? 'active' : ''}`}
              data-testid="maintenance-toggle"
            >
              <Power className="h-4 w-4" />
              {maintenanceMode ? 'Site Offline' : 'Site Live'}
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="admin-tabs">
          <button 
            className={`admin-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleTabChange('dashboard')}
            data-testid="tab-dashboard"
          >
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </button>
          <button 
            className={`admin-tab ${activeTab === 'respond' ? 'active' : ''}`}
            onClick={() => handleTabChange('respond')}
            data-testid="tab-respond"
          >
            <Mail className="h-4 w-4" />
            Generate Response
          </button>
          <button 
            className={`admin-tab ${activeTab === 'tickets' ? 'active' : ''}`}
            onClick={() => handleTabChange('tickets')}
            data-testid="tab-tickets"
          >
            <Inbox className="h-4 w-4" />
            Tickets
          </button>
          <button 
            className={`admin-tab ${activeTab === 'emails' ? 'active' : ''}`}
            onClick={() => handleTabChange('emails')}
            data-testid="tab-emails"
          >
            <Send className="h-4 w-4" />
            Sent Emails
            {emails.length > 0 && (
              <span className="tab-badge">{emails.length}</span>
            )}
          </button>
          <button 
            className={`admin-tab ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => handleTabChange('messages')}
            data-testid="tab-messages"
          >
            <MessageSquare className="h-4 w-4" />
            Messages
            {(() => {
              const newCount = messages.filter(m => m.status !== 'replied').length;
              return newCount > 0 ? <span className="tab-badge">{newCount}</span> : null;
            })()}
          </button>
          <button 
            className={`admin-tab ${activeTab === 'tezza' ? 'active' : ''}`}
            onClick={() => handleTabChange('tezza')}
            data-testid="tab-tezza"
          >
            <Bot className="h-4 w-4" />
            Tezza AI
            {tezzaStats?.successful_links_sent > 0 && (
              <span className="tab-badge" style={{ background: '#22c55e' }}>{tezzaStats.successful_links_sent}</span>
            )}
          </button>
          <button 
            className={`admin-tab ${activeTab === 'payments' ? 'active' : ''}`}
            onClick={() => handleTabChange('payments')}
            data-testid="tab-payments"
          >
            <CreditCard className="h-4 w-4" />
            Payments
            <span className="tab-badge" style={{ background: '#22c55e' }}>{paymentsTotal}</span>
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <AdminDashboardTab
            loadingStats={loadingStats}
            stats={stats}
            emails={emails}
            formatDate={formatDate}
            getPriorityColor={getPriorityColor}
            password={password}
          />
        )}

        {activeTab === 'respond' && (
          <AdminResponseTab
            password={password}
            fetchStats={fetchStats}
            formatDate={formatDate}
            getPriorityColor={getPriorityColor}
            initialEmail={responseInitial.email}
            initialSubject={responseInitial.subject}
            initialMessage={responseInitial.message}
            initialTimestamp={responseInitial.timestamp}
          />
        )}

        {activeTab === 'tickets' && (
          <AdminTicketsTab
            tickets={tickets}
            formatDate={formatDate}
            getPriorityColor={getPriorityColor}
          />
        )}

        {activeTab === 'emails' && (
          <AdminEmailsTab
            emails={emails}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'messages' && (
          <AdminMessagesTab
            messages={messages}
            formatDate={formatDate}
            onGenerateResponse={handleGenerateResponseFromMessage}
          />
        )}

        {activeTab === 'tezza' && (
          <AdminTezzaTab
            tezzaActivity={tezzaActivity}
            tezzaStats={tezzaStats}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'payments' && (
          <AdminPaymentsTab
            password={password}
            payments={payments}
            paymentsTotal={paymentsTotal}
            fetchStats={fetchStats}
            formatDate={formatDate}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPage;
